/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/itp-export/QaItpSizeDialog.tsx
 * FILE_ID: JOBHUB-QA-ITP-SIZE-DIALOG-001
 * PURPOSE: Ask whether a PDF over 20 MB goes as a link or an attachment.
 */
import { useEffect } from 'react'
import { MobileSheet, useIsMdUp } from '@quotech/ui'

// Reused from: packages/job-hub/src/jobs/tabs/job-qa/QaUseTemplatesDialog.tsx (dialog card)

export function QaItpSizeDialog({
  open,
  sizeMb,
  linkAvailable,
  onLink,
  onAttach,
  onCancel,
}: {
  open: boolean
  sizeMb: number
  linkAvailable: boolean
  onLink: () => void
  onAttach: () => void
  onCancel: () => void
}) {
  const desktop = useIsMdUp()
  useEffect(() => {
    if (!open || !desktop) return undefined
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCancel()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [desktop, onCancel, open])
  if (!open) return null
  const buttons = (
    <div className="mt-4 flex flex-col gap-2">
      <button
        type="button"
        disabled={!linkAvailable}
        onClick={onLink}
        className="min-h-[44px] rounded-md bg-primary px-2.5 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50 md:min-h-0"
      >
        Send as OneDrive link
      </button>
      {!linkAvailable ? (
        <p className="text-xs text-muted-foreground">Links need an Outlook mailbox in From.</p>
      ) : null}
      <button
        type="button"
        onClick={onAttach}
        className="min-h-[44px] rounded-md border border-border px-2.5 py-1.5 text-xs font-medium md:min-h-0"
      >
        Attach anyway
      </button>
      <button
        type="button"
        onClick={onCancel}
        className="min-h-[44px] rounded-md px-2.5 py-1.5 text-xs text-muted-foreground md:min-h-0"
      >
        Cancel
      </button>
    </div>
  )
  const copy = (
    <>
      <p className="mt-2 text-sm text-muted-foreground">
        Files over 20 MB are best sent as a link. You can still attach it.
      </p>
      {buttons}
    </>
  )
  if (!desktop) {
    return (
      <MobileSheet open={open} onClose={onCancel} title={`This PDF is ${sizeMb} MB`}>
        {copy}
      </MobileSheet>
    )
  }
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-background/70 p-4" role="presentation" onClick={onCancel}>
      <div
        role="dialog"
        aria-labelledby="qa-itp-size-title"
        className="w-full max-w-sm rounded-xl border border-border bg-card p-4 shadow-lg"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 id="qa-itp-size-title" className="text-sm font-semibold text-foreground">
          {`This PDF is ${sizeMb} MB`}
        </h2>
        {copy}
      </div>
    </div>
  )
}
